import { useEffect, useState } from 'react';

import { css } from '@flipster/styled-system/css';
import { hstack } from '@flipster/styled-system/patterns';
import { numberFormatter } from '@flipster/utils/num';

export interface SparksInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onInput'> {
  value?: number;
  onInput?: (value: number) => void;
}

function formatSparks(sparks: string): string {
  const digits = sparks.replace(/[^0-9]/g, '');
  const number = digits.replace(/^0+/, '');
  const localeNumber = numberFormatter(number);
  if (localeNumber === '0') return '';
  return localeNumber;
}

function backspaceString(str: string, offset: number): string {
  return str.slice(0, offset - 1) + str.slice(offset);
}

const SparksInput = ({ value: valueProp, onInput: onInputProp, ...props }: SparksInputProps) => {
  const [effects, setEffects] = useState<(() => void)[]>([]);
  useEffect(() => {
    effects.forEach((effect) => effect());
    effects.length = 0;
  }); // 매 렌더마다 불려야 하는 이펙트이기 때문에 deps array 생략함

  const value = formatSparks(String(valueProp || ''));
  function onInput(e: React.FormEvent<HTMLInputElement>) {
    const input = e.currentTarget;
    const inputValue = input.value;
    let nextValue = formatSparks(inputValue);
    let prevCaret = input.selectionStart || 0;
    if (value === nextValue && inputValue.length < nextValue.length) {
      nextValue = backspaceString(nextValue, prevCaret);
      prevCaret -= 1;
    }
    const diff = inputValue.length - nextValue.length;
    setEffects([
      () => {
        const caret = Math.max(prevCaret - diff, 0);
        input.setSelectionRange(caret, caret);
      },
    ]);
    onInputProp?.(Number(nextValue.replace(/,/g, '') || '0'));
  }

  return (
    <div
      className={css({
        pos: 'relative',
        textStyle: 'Title/M',
        color: 'content.default.level-4',
        fontVariantNumeric: 'tabular-nums',
      })}
    >
      <label className={hstack({ gap: 6, color: 'transparent', cursor: 'text' })}>
        <span className={hstack({ flex: '1 0 0' })}>
          {value ? null : <span className={css({ pointerEvents: 'none', userSelect: 'none' })}>0</span>}
          <input
            {...props}
            type='text'
            inputMode='numeric'
            step={1}
            maxLength={16}
            value={value}
            onInput={onInput}
            className={css({
              flexGrow: 1,
              w: 0,
              minW: 0,
              textStyle: 'Title/M',
              textAlign: 'center',
              appearance: 'none',
              color: 'transparent',
              caretColor: 'content.accent.info',
              fontVariantNumeric: 'tabular-nums',
            })}
          />
        </span>
        <span className={css({ flexShrink: 0, pointerEvents: 'none', userSelect: 'none' })}>Sparks</span>
      </label>
      <div
        className={hstack({
          justifyContent: 'center',
          gap: 6,
          pos: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          userSelect: 'none',
        })}
      >
        <span
          data-is-zero={!value ? '' : undefined}
          className={css({ '&[data-is-zero]': { color: 'content.default.level-2' } })}
        >
          {value || '0'}
        </span>
        <span>Sparks</span>
      </div>
    </div>
  );
};

export default SparksInput;
